import React, { useEffect, useState } from "react";
import { seoApi } from "@/lib/seoApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { Copy, AlertTriangle, RefreshCw, Download, Search } from "lucide-react";
import { toast } from "sonner";

const CAT_COLOR = {
  Provider:      "bg-blue-100 text-blue-800",
  Configuration: "bg-purple-100 text-purple-800",
  Validation:    "bg-amber-100 text-amber-800",
  Network:       "bg-cyan-100 text-cyan-800",
  Timeout:       "bg-orange-100 text-orange-800",
  Authentication: "bg-red-100 text-red-800",
  Budget:        "bg-red-100 text-red-900 font-semibold",
  Internal:      "bg-neutral-200 text-neutral-800",
  User:          "bg-yellow-100 text-yellow-800",
  Unknown:       "bg-neutral-100 text-neutral-600",
};
const SEV_COLOR = { critical: "bg-red-100 text-red-800",
                     error: "bg-red-50 text-red-700",
                     warning: "bg-amber-50 text-amber-700" };

export default function ErrorCenter() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState("");
  const [q, setQ] = useState("");
  const [days, setDays] = useState(7);

  async function load() {
    const params = new URLSearchParams({ days: String(days), limit: "300" });
    if (category) params.set("category", category);
    if (severity) params.set("severity", severity);
    if (q) params.set("q", q);
    const [errRes, statsRes] = await Promise.all([
      seoApi.get(`/seo/ops/errors?${params}`),
      seoApi.get(`/seo/ops/errors/stats?days=${days}`),
    ]);
    setItems(errRes.data.items || []);
    setStats(statsRes.data);
  }
  useEffect(() => { load(); }, [days, category, severity, q]);

  function copyStack(msg) {
    navigator.clipboard.writeText(msg);
    toast.success("Copied to clipboard");
  }

  return (
    <div className="p-6" data-testid="error-center">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <AlertTriangle size={22} /> Error Center
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Every stage-level, run-level, and event-log error — classified, filterable, retryable.
          </p>
        </div>
        <a href={`${window.__API || ""}/api/seo/ops/reports/errors?fmt=csv&days=${days}`}
           target="_blank" rel="noreferrer">
          <Button size="sm" variant="outline" className="gap-2" data-testid="btn-export-errors">
            <Download size={14} /> Export CSV
          </Button>
        </a>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="error-stats">
          <StatCard label="Total errors"    v={stats.total_errors} />
          <StatCard label="Runs (window)"   v={stats.runs_total} />
          <StatCard label="Failed"          v={stats.runs_failed} tone="danger" />
          <StatCard label="Recovered"       v={stats.runs_recovered} tone="positive" />
          <StatCard label="Failure rate"    v={`${stats.failure_rate_pct}%`}
                     tone={stats.failure_rate_pct >= 20 ? "danger" : "neutral"} />
          <StatCard label="Recovery rate"   v={`${stats.recovery_rate_pct}%`} tone="positive" />
        </div>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input placeholder="Search messages, run IDs…" value={q} onChange={(e) => setQ(e.target.value)}
                     className="pl-9" data-testid="filter-search" />
          </div>
          <select className="border rounded px-2 py-1.5 text-sm" value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid="filter-category">
            <option value="">All categories</option>
            {["Provider","Configuration","Validation","Network","Timeout","Authentication","Budget","Internal","User","Unknown"].map((c) =>
              <option key={c} value={c}>{c}</option>
            )}
          </select>
          <select className="border rounded px-2 py-1.5 text-sm" value={severity}
                    onChange={(e) => setSeverity(e.target.value)}
                    data-testid="filter-severity">
            <option value="">All severity</option>
            <option value="critical">Critical</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
          </select>
          <select className="border rounded px-2 py-1.5 text-sm" value={days}
                    onChange={(e) => setDays(+e.target.value)}
                    data-testid="filter-days">
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7d</option>
            <option value={30}>Last 30d</option>
            <option value={90}>Last 90d</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-reload">
            <RefreshCw size={14} /> Reload
          </Button>
        </CardContent>
      </Card>

      {/* Error list */}
      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="p-10 text-center text-neutral-500 text-sm">No errors in the selected window ✓</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-neutral-500">
                <tr className="border-b">
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-2 py-2">Category</th>
                  <th className="text-left px-2 py-2">Severity</th>
                  <th className="text-left px-2 py-2">Service</th>
                  <th className="text-left px-2 py-2">Stage</th>
                  <th className="text-left px-2 py-2">Run</th>
                  <th className="text-left px-2 py-2">Message</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className="border-t border-neutral-100 hover:bg-neutral-50"
                        data-testid={`err-row-${e.category.toLowerCase()}`}>
                    <td className="px-3 py-2 text-xs text-neutral-500 tabular-nums whitespace-nowrap">
                      {e.at ? new Date(e.at).toLocaleString([], { hour12: false }) : "—"}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${CAT_COLOR[e.category] || CAT_COLOR.Unknown}`}>
                        {e.category}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${SEV_COLOR[e.severity] || SEV_COLOR.error}`}>
                        {e.severity}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-xs">{e.service || "—"}</td>
                    <td className="px-2 py-2 text-xs">{e.stage_name || <span className="text-neutral-400">—</span>}</td>
                    <td className="px-2 py-2 text-xs font-mono">
                      {e.run_id ? (
                        <Link to={`/seo-studio/runs/${e.run_id}`} className="underline hover:no-underline">
                          {e.run_id.slice(0, 8)}
                        </Link>
                      ) : "—"}
                    </td>
                    <td className="px-2 py-2 text-xs max-w-md truncate" title={e.message}>{e.message}</td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button onClick={() => copyStack(e.stack || e.message)}
                                className="text-xs underline hover:no-underline text-neutral-500 hover:text-neutral-900"
                                data-testid="btn-copy-stack">
                        <Copy size={12} className="inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, v, tone = "neutral" }) {
  const cls = {
    positive: "border-emerald-200 bg-emerald-50/30",
    warning:  "border-amber-200 bg-amber-50/30",
    danger:   "border-red-200 bg-red-50/30",
    neutral:  "border-neutral-200",
  }[tone];
  return (
    <div className={`border rounded-lg p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className="text-2xl font-semibold tabular-nums text-neutral-900 mt-1">{v}</div>
    </div>
  );
}
